import TicketTypes = Boo.Objects.Enums.TicketTypeEnum;
import ComponentConfig from 'app/ticket/config/ComponentConfig';
import FileSections = Boo.Objects.Enums.FileSectionTypesEnum;

/**
 * Class to configure components per ticket type
 */
export default class TicketTypeComponents {
    /**
     * Returns a list of components for the ticket and specified parameters,
     * or the default set of components if a specific listing is not available.
     */
    public static get(ticketType: Boo.Objects.TicketType, partnerId?: number): app.ticket.interfaces.IComponentConfig[] {
        // Do not add to this pattern. If we need additional functionality, we should probably rework the configuration
        // to support specific defaults and optional overrides. We probably want overrides that support component addition,
        // removal, and changes. Currently this is all quite messy and spreads factory/config logic between this
        // factory and the TicketTypeComponents configuration.

        const componentConfig: app.ticket.interfaces.IComponentConfig[] =
            TicketTypeComponents.getByPartnerAndTicketType(partnerId, ticketType)
            || TicketTypeComponents.getByTicketType(ticketType)
            || TicketTypeComponents.getDefaults(ticketType);

        return componentConfig;
    }

    private static getDefaults(ticketType: Boo.Objects.TicketType): app.ticket.interfaces.IComponentConfig[] {
        const components = [
            new ComponentConfig('BasicInfo', ({ isChangingDatesAllowed: ticketType.IsChangingDatesAllowed ? true : false } as app.ticket.interfaces.IBasicInfoComponentFeatureConfig)),
            new ComponentConfig('Descendants', ({ isApprovalRequired: ticketType.IsApprovalRequired ? true : false } as app.ticket.interfaces.IDescendantsComponentFeatureConfig)),
            new ComponentConfig('ContactAttempts'),
            new ComponentConfig('Review'),
            new ComponentConfig('ConnectedUsersFloatingPanel'),
            new ComponentConfig('NoteFloatingPanel'),
            new ComponentConfig('CustomerTodoListFloatingPanel'),
            new ComponentConfig('Files', ({
                availableFileSections: [
                    FileSections.General
                ],
                defaultFileSection: FileSections.General,
                canDelete: false,
                canUpload: ticketType.HasFileUploadCapability ? true : false,
                isFileUploadRequired: ticketType.IsFileUploadRequired
            } as app.ticket.interfaces.IFilesComponentFeatureConfig))
        ];

        if (ticketType.IsApprovalRequired) {
            components.splice(1, 0, new ComponentConfig('Features', ({
                isApprovalRequired: ticketType.IsApprovalRequired ? true : false,
                hasFileUploadCapability: ticketType.HasFileUploadCapability ? true : false,
                isFileUploadRequired: ticketType.IsFileUploadRequired ? true : false,
                approvalQuestionText: ticketType.ApprovalQuestionText || ''
            } as app.ticket.interfaces.IFeaturesComponentFeatureConfig)));
        }

        return components;
    }

    /**
     * Returns a list of components by ticket type id.
     * Set up components that apply to all partners here.
     */
    private static getByTicketType(ticketType: Boo.Objects.TicketType): app.ticket.interfaces.IComponentConfig[] {
        const defaults = TicketTypeComponents.getDefaults(ticketType);

        let components: app.ticket.interfaces.IComponentConfig[] = null;
        switch (ticketType.TicketTypeId) {
            case TicketTypes.BoostListingsOnboarding:
                components = this.extendDefaults(ticketType, [
                    new ComponentConfig('ContactAttempts'),
                    new ComponentConfig('LockConfirmation', ({ lockOnAppointment: true } as app.ticket.interfaces.ILockConfirmationComponentFeatureConfig))
                ]);
                break;
            case TicketTypes.SeoOnboarding:
                components = [
                    new ComponentConfig('LockConfirmation', ({ lockOnAppointment: true } as app.ticket.interfaces.ILockConfirmationComponentFeatureConfig)),
                    new ComponentConfig('BasicInfo', ({ isChangingDatesAllowed: ticketType.IsChangingDatesAllowed ? true : false, requireAppointmentToChangeAssignedUser: true } as app.ticket.interfaces.IBasicInfoComponentFeatureConfig)),
                    new ComponentConfig('Review'),
                    new ComponentConfig('ContactAttempts'),
                    new ComponentConfig('ConnectedUsersFloatingPanel'),
                    new ComponentConfig('CustomerTodoListFloatingPanel'),
                    new ComponentConfig('WebsiteAudit'),
                    new ComponentConfig('EmailSettings'),
                    new ComponentConfig('KeywordResearch'),
                    new ComponentConfig('LocalProfileInitialization'),
                    new ComponentConfig('SeoContentSurvey'),
                    new ComponentConfig('logins-and-publish-options'),
                    new ComponentConfig('LinkBuilding'),
                    new ComponentConfig('Files', ({
                        availableFileSections: [FileSections.General, FileSections.KeywordResearch],
                        defaultFileSection: FileSections.KeywordResearch,
                        canDelete: false,
                        canUpload: ticketType.HasFileUploadCapability ? true : false,
                        isFileUploadRequired: ticketType.IsFileUploadRequired,
                        filterByTicket: false
                    } as app.ticket.interfaces.IFilesComponentFeatureConfig)),
                    new ComponentConfig('Descendants', ({ isApprovalRequired: ticketType.IsApprovalRequired ? true : false } as app.ticket.interfaces.IDescendantsComponentFeatureConfig)),
                    new ComponentConfig('TicketNavigationFloatingPanel', ({
                        navigationLinks: [
                            { id: 'BasicInfo', text: 'Basic Information' },
                            { id: 'Review', text: 'Review' },
                            { id: 'ContactAttempts', text: 'Contact Attempts' },
                            { id: 'WebsiteAudit', text: 'Website Audit' },
                            { id: 'EmailSettings', text: 'Email Settings' },
                            { id: 'KeywordResearch', text: 'Keyword Research' },
                            { id: 'LocalProfileInitialization', text: 'Local Profiles' },
                            { id: 'SeoContentSurvey', text: 'SEO Content Survey' },
                            { id: 'logins-and-publish-options', text: 'Publish Options' },
                            { id: 'LinkBuilding', text: 'Link Building' },
                            { id: 'Files', text: 'Files' }
                        ]
                    } as app.ticket.interfaces.ITicketNavigationComponentFeatureConfig))
                ];
                break;
            case TicketTypes.FulfillmentWorkblock:
            case TicketTypes.BusinessProfileWorkblock:
            case TicketTypes.SendOnsiteBlogPostToCustomer:
                components = defaults;
                break;
            case TicketTypes.MonthlyCheckup:
                components = [
                    new ComponentConfig('BasicInfo', ({ isChangingDatesAllowed: ticketType.IsChangingDatesAllowed ? true : false } as app.ticket.interfaces.IBasicInfoComponentFeatureConfig)),
                    new ComponentConfig('Descendants', ({ isApprovalRequired: ticketType.IsApprovalRequired ? true : false } as app.ticket.interfaces.IDescendantsComponentFeatureConfig)),
                    new ComponentConfig('ContactAttempts'),
                    new ComponentConfig('Review'),
                    new ComponentConfig('NoteFloatingPanel'),
                    new ComponentConfig('CustomerTodoListFloatingPanel'),
                    new ComponentConfig('ReportingAccess'),
                    new ComponentConfig('logins-and-publish-options'),
                    new ComponentConfig('CustomActionsReport'),
                    new ComponentConfig('OnsiteRecommendationsComplete'),
                    new ComponentConfig('Files', ({
                        availableFileSections: [
                            FileSections.General
                        ],
                        defaultFileSection: FileSections.General,
                        canDelete: false,
                        canUpload: ticketType.HasFileUploadCapability ? true : false,
                        isFileUploadRequired: ticketType.IsFileUploadRequired
                    } as app.ticket.interfaces.IFilesComponentFeatureConfig)),
                    new ComponentConfig('ContactAttempts'),
                    new ComponentConfig('CustomReports'),
                ];
                break;
            case TicketTypes.ProactiveUnderPerformingKeyword:
                components = [
                    new ComponentConfig('BasicInfo', ({ isChangingDatesAllowed: ticketType.IsChangingDatesAllowed ? true : false } as app.ticket.interfaces.IBasicInfoComponentFeatureConfig)),
                    new ComponentConfig('Descendants', ({ isApprovalRequired: ticketType.IsApprovalRequired ? true : false } as app.ticket.interfaces.IDescendantsComponentFeatureConfig)),
                    new ComponentConfig('ContactAttempts'),
                    new ComponentConfig('Review'),
                    new ComponentConfig('NoteFloatingPanel'),
                    new ComponentConfig('CustomerTodoListFloatingPanel'),
                    new ComponentConfig('logins-and-publish-options'),
                    new ComponentConfig('customer-campaign'),
                    new ComponentConfig('upa-checklist'),
                    new ComponentConfig('Files', ({
                        availableFileSections: [
                            FileSections.General,
                            FileSections.BoostSite,
                            FileSections.LocalProfile,
                            FileSections.Marketing,
                            FileSections.Ranks
                        ],
                        defaultFileSection: FileSections.General,
                        canDelete: true,
                        canUpload: ticketType.HasFileUploadCapability ? true : false,
                        isFileUploadRequired: ticketType.IsFileUploadRequired
                    } as app.ticket.interfaces.IFilesComponentFeatureConfig))
                ];
                break;
            case TicketTypes.OnsiteCopySendCopy:
            case TicketTypes.OnsiteCopyPublished:
                components = this.getDefaults(ticketType);
                components.splice(1, 0,
                  new ComponentConfig('OnsiteCopyRequest'),
                  new ComponentConfig('customer-campaign')
                );
                break;
            case TicketTypes.OnsiteRecommendationReview:
                components = this.getDefaults(ticketType);
                components.splice(1, 0, new ComponentConfig('customer-campaign', ({ reviewSource: Boo.OnsiteRecommendations.Models.Enums.ReviewSources.ReviewTicket } as app.ticket.interfaces.ICustomerCampaignComponentFeatureConfig)));
                break;
            case TicketTypes.OnsiteRecommendationsNotImplemented:
                components = this.getDefaults(ticketType);
                components.splice(1, 0, new ComponentConfig('customer-campaign', ({ reviewSource: Boo.OnsiteRecommendations.Models.Enums.ReviewSources.NotImplementedTicket } as app.ticket.interfaces.ICustomerCampaignComponentFeatureConfig))
                );
                break;
            case TicketTypes.ManageBusinessCitationSubscriptions:
                components = defaults.concat([
                    new ComponentConfig('LocalProfiles')
                ]);
                break;
            case TicketTypes.OnsiteCopyReadyForPreview:
                components = this.extendDefaults(
                    ticketType,
                    [
                      new ComponentConfig('OnsiteCopyImplementationNotes'),
                      new ComponentConfig('blog-and-copy-content-review'),
                      new ComponentConfig('SeoContentSurvey')
                    ],
                    true
                );
                break;
            case TicketTypes.OnsiteBlogReadyForPreview:
                components = defaults.concat([
                  new ComponentConfig('blog-and-copy-content-review'),
                  new ComponentConfig('SeoContentSurvey')
                ]);
                break;
            case TicketTypes.Retention:
                components = defaults.concat([
                    new ComponentConfig('Retention')
                  ]);
                break;
            case TicketTypes.KeywordResearchStrategyUpdate:
                  components = [
                    new ComponentConfig('BasicInfo', ({ isChangingDatesAllowed: ticketType.IsChangingDatesAllowed ? true : false } as app.ticket.interfaces.IBasicInfoComponentFeatureConfig)),
                    new ComponentConfig('Descendants', ({ isApprovalRequired: ticketType.IsApprovalRequired ? true : false } as app.ticket.interfaces.IDescendantsComponentFeatureConfig)),
                    new ComponentConfig('ContactAttempts'),
                    new ComponentConfig('Review'),
                    new ComponentConfig('ConnectedUsersFloatingPanel'),
                    new ComponentConfig('NoteFloatingPanel'),
                    new ComponentConfig('CustomerTodoListFloatingPanel'),
                    new ComponentConfig('StrategyUpdate'),
                    new ComponentConfig('Files', ({
                      availableFileSections: [FileSections.KeywordResearch],
                      defaultFileSection: FileSections.KeywordResearch,
                      canDelete: false,
                      canUpload: ticketType.HasFileUploadCapability ? true : false,
                      isFileUploadRequired: ticketType.IsFileUploadRequired,
                      filterByTicket: false
                    } as app.ticket.interfaces.IFilesComponentFeatureConfig))
                  ];
                  break;
            case TicketTypes.InvalidUrl:
                components = this.getDefaults(ticketType);
                components.splice(1, 0,
                    new ComponentConfig('invalid-url')
                );
                break;
            case TicketTypes.LocalSeoStarterIncompleteOnboarding:
                components = this.getDefaults(ticketType);
                components.splice(1, 0,
                    new ComponentConfig('seo-starter-onboarding-link')
                );
                break;
            default:
                components = null;
                break;
        }
        return components;
    }

    /**
     * Returns a list of components by ticket type with partner conditions
     * Set up components that are specific to partners here.
     */
    private static getByPartnerAndTicketType(partnerId: number, ticketType: Boo.Objects.TicketType): app.ticket.interfaces.IComponentConfig[] {
        let components: app.ticket.interfaces.IComponentConfig[] = null;
        switch (partnerId) {
            case Boo.Objects.Enums.PartnerEnum.Boostability:
                switch (ticketType.TicketTypeId) {
                    case TicketTypes.SeoOnboarding:
                        // Adds KeywordResearch File type for Boostability partner
                        components = [
                            new ComponentConfig('LockConfirmation', ({ lockOnAppointment: true } as app.ticket.interfaces.ILockConfirmationComponentFeatureConfig)),
                            new ComponentConfig('BasicInfo', ({ isChangingDatesAllowed: ticketType.IsChangingDatesAllowed ? true : false, requireAppointmentToChangeAssignedUser: true } as app.ticket.interfaces.IBasicInfoComponentFeatureConfig)),
                            new ComponentConfig('Review'),
                            new ComponentConfig('ContactAttempts'),
                            new ComponentConfig('ConnectedUsersFloatingPanel'),
                            new ComponentConfig('CustomerTodoListFloatingPanel'),
                            new ComponentConfig('WebsiteAudit'),
                            new ComponentConfig('EmailSettings'),
                            new ComponentConfig('KeywordResearch'),
                            new ComponentConfig('LocalProfileInitialization'),
                            new ComponentConfig('SeoContentSurvey'),
                            new ComponentConfig('logins-and-publish-options'),
                            new ComponentConfig('LinkBuilding'),
                            new ComponentConfig('Files', ({
                                availableFileSections: [FileSections.General, FileSections.KeywordResearch],
                                defaultFileSection: FileSections.KeywordResearch,
                                canDelete: false,
                                canUpload: ticketType.HasFileUploadCapability ? true : false,
                                isFileUploadRequired: ticketType.IsFileUploadRequired,
                                filterByTicket: false
                            } as app.ticket.interfaces.IFilesComponentFeatureConfig)),
                            new ComponentConfig('Descendants', ({ isApprovalRequired: ticketType.IsApprovalRequired ? true : false } as app.ticket.interfaces.IDescendantsComponentFeatureConfig)),
                            new ComponentConfig('NoteFloatingPanel'),
                            new ComponentConfig('TicketNavigationFloatingPanel', ({
                                navigationLinks: [
                                    { id: 'BasicInfo', text: 'Basic Information' },
                                    { id: 'Review', text: 'Review' },
                                    { id: 'ContactAttempts', text: 'Contact Attempts' },
                                    { id: 'WebsiteAudit', text: 'Website Audit' },
                                    { id: 'EmailSettings', text: 'Email Settings' },
                                    { id: 'KeywordResearch', text: 'Keyword Research' },
                                    { id: 'LocalProfileInitialization', text: 'Local Profile' },
                                    { id: 'SeoContentSurvey', text: 'SEO Content Survey' },
                                    { id: 'logins-and-publish-options', text: 'Publish Options' },
                                    { id: 'LinkBuilding', text: 'Link Building' },
                                    { id: 'Files', text: 'Files' }
                                ]
                            } as app.ticket.interfaces.ITicketNavigationComponentFeatureConfig))
                        ];
                        break;
                    default:
                        components = null;
                        break;
                }
                break;
            default:
                components = null;
                break;
        }
        return components;
    }

    private static extendDefaults(ticketType: Boo.Objects.TicketType, components: app.ticket.interfaces.IComponentConfig[], appendToDefaults = false): app.ticket.interfaces.IComponentConfig[] {
        const defaults = TicketTypeComponents.getDefaults(ticketType);
        const result: app.ticket.interfaces.IComponentConfig[] = [];

        // Include defaults if not already in list
        _.each(defaults, (defaultComponent: app.ticket.interfaces.IComponentConfig) => {
            const matchingComponent = _.find(components, (overriddenComponent: app.ticket.interfaces.IComponentConfig) => {
                return overriddenComponent.name === defaultComponent.name;
            });
            if (!matchingComponent) {
                result.push(defaultComponent);
            }
        });

        if (appendToDefaults) {
            result.push(...components);
        } else {
            result.unshift(...components);
        }

        return result;
    }
}
